// The Building and Property pages' portfolio overviews (GET /building/overview, GET /property/overview;
// src/modules/portfolio/buildingOverview.service.ts, src/modules/maintenance/propertyOverview.service.ts).
// A null block: the reader's role cannot read the page it comes from.
import { api } from './api';
import { formatMoney } from './viz';
import type { BuildingGlanceLink, BuildingSummary } from '../types/api';

export interface MoneyByCurrency { currency: string; minor: number }
export interface CertRoll { expired: number; due: number; valid: number; missing: number }

export interface DevelopmentBuilding {
  propertyId: string; name: string; region: string; city: string | null;
  recordPct: number | null; overdue: number | null; dueIn90: number | null; certs: CertRoll | null;
  assets: number | null; assetsDue: number | null; contractsPaMinor: number | null; currency: string;
}
export interface OverdueDate { id: string; propertyId: string; propertyName: string; date: string; label: string; sub: string | null; source: string; daysAgo: number; link: BuildingGlanceLink }
export type DevelopmentBudget = NonNullable<BuildingSummary['money']> & { propertyId: string; name: string; units: string | null };
export interface BuildingOverview {
  visible: { record: boolean; assets: boolean; contracts: boolean; certificates: boolean; budgets: boolean };
  stats: { developments: number; overdue: number | null; dueIn90: number | null; certsExpired: number | null; assets: number | null; assetsDue: number | null; underContractPa: MoneyByCurrency[] | null };
  developments: DevelopmentBuilding[];
  overdue: OverdueDate[] | null;
  budgets: DevelopmentBudget[] | null;
}

export interface DevelopmentProperty {
  propertyId: string; name: string; region: string; city: string | null;
  open: number | null; overdue: number | null; emergency: number | null; completed: number | null; certs: CertRoll | null; visitsDue: number | null;
}
export interface OpenJob {
  id: string; reference: string; title: string; propertyId: string; propertyName: string; unitId: string | null; unitReference: string | null;
  category: string | null; priority: string; status: string; resolveBy: string | null; overdue: boolean; vendorName: string | null; assignedToName: string | null;
}
export interface PropertyOverview {
  visible: { works: boolean; certificates: boolean; visits: boolean; bills: boolean; arrears: boolean };
  stats: {
    developments: number; openWorks: number | null; overdue: number | null; emergency: number | null; certsExpired: number | null; renewalsDue: number | null;
    worksSpend: MoneyByCurrency[] | null; workOrdersOpen: number | null; invoicesUnpaid: { count: number; owed: MoneyByCurrency[] } | null;
  };
  developments: DevelopmentProperty[];
  openWork: OpenJob[] | null;
  arrears: { count: number; owed: MoneyByCurrency[] } | null;
}

export const buildingOverview = async () => (await api.get<BuildingOverview>('/building/overview')).data;
export const propertyOverview = async () => (await api.get<PropertyOverview>('/property/overview')).data;

/** Where a key date lives on its development's page (the same places the Building management tab's rows go). */
export function hubLink(propertyId: string, l: BuildingGlanceLink): string {
  if (l.assetId) return `/app/assets/${l.assetId}`;
  const q = new URLSearchParams({ tab: l.tab });
  if (l.sub !== 'glance') q.set('sub', l.sub);
  if (l.sectionId) q.set('section', l.sectionId);
  if (l.contractId) q.set('contract', l.contractId);
  return `/app/properties/${propertyId}?${q.toString()}`;
}

/** The biggest currency first, the rest as "+ AED 96,000" in the hint. */
export function moneyTile(m: MoneyByCurrency[] | null | undefined, empty = '—'): { value: string; hint?: string } {
  if (!m?.length) return { value: empty };
  return { value: formatMoney(m[0]!.minor, m[0]!.currency), hint: m.length > 1 ? m.slice(1).map((x) => `+ ${formatMoney(x.minor, x.currency)}`).join(' ') : undefined };
}
export const tileRow = { gridTemplateColumns: 'repeat(auto-fit, minmax(118px, 1fr))' };

// ── The Accounts page's Overview (GET /accounts/overview; src/modules/finance/accountsOverview.service.ts) ──
export interface CountAndMoney { count: number; amount: MoneyByCurrency[] }
/** A "By development" row; the fee columns are null for a role without fees.read. */
export interface AccountsRow {
  currency: string; let: number; lettable: number; rentRollPaMinor: number; arrearsMinor: number; buildingCostsMinor: number;
  managementMinor: number | null; lettingKeptMinor: number | null; lettingPaidAwayMinor: number | null; netToUsMinor: number | null;
}
export interface DevelopmentAccounts extends AccountsRow { propertyId: string; name: string; region: string; city: string | null; feeBasis: string | null }
export interface AccountsOverview {
  visible: { fees: boolean; arrears: boolean; deposits: boolean; banking: boolean; bills: boolean };
  stats: {
    developments: number; rentRollPa: MoneyByCurrency[]; arrears: MoneyByCurrency[]; buildingCostsPa: MoneyByCurrency[];
    feeIncomePa: MoneyByCurrency[] | null; paidToAgents: MoneyByCurrency[] | null; netToUsPa: MoneyByCurrency[] | null;
    invoicesUnpaid: CountAndMoney | null; agentInvoicesWaiting: CountAndMoney | null; depositsHeld: CountAndMoney | null; unallocated: CountAndMoney | null;
  };
  developments: DevelopmentAccounts[];
  totals: AccountsRow[];
  chart: Array<{ currency: string; months: Array<{ month: string; label: string; rentMinor: number; spendMinor: number }> }>;
  arrears: CountAndMoney | null;
}
export const accountsOverview = async () => (await api.get<AccountsOverview>('/accounts/overview')).data;
/** Several currencies in a sentence: "£4,800 + AED 1,200". */
export const moneyList = (m: MoneyByCurrency[]) => m.map((x) => formatMoney(x.minor, x.currency)).join(' + ');
